import type { CSSProperties, ReactNode } from "react";

import { cn } from "@btnet/utilities-core";

import "./BuiHeader.less";

/**
 * Props for the BuiHeader component.
 */
export interface IBuiHeaderProps {
  /**
   * The aria-label for the header accessibility.
   */
  ariaLabel: string;

  /**
   * Optional children elements to be rendered within the header.
   */
  children?: ReactNode;

  /**
   * Optional className for custom styling.
   */
  className?: string;

  /**
   * The test ID for the header component.
   */
  testid: string;

  /**
   * Optional inline styles for the header component.
   */
  style?: CSSProperties;
}

/**
 * The base header component for Blueprint design system.
 *
 * @example
 * ```tsx
 * <BuiHeader ariaLabel="Main Header" className="custom-header" testid="bds-header-1">
 *     <h1>Welcome to the Application</h1>
 * </BuiHeader>
 * ```
 *
 * @param props - BuiHeader properties.
 * @param props.ariaLabel - Accessible label for the header.
 * @param props.children - Child elements to be rendered inside the header.
 * @param props.className - Additional CSS classes for custom styling.
 * @param props.testid - Test identifier for testing purposes.
 *
 * @returns The BuiHeader component.
 */
export const BuiHeader = (props: IBuiHeaderProps) => {
  const { ariaLabel, children, className, testid, style } = props;

  return (
    <header
      role="banner"
      className={cn("bui-header", className)}
      aria-label={ariaLabel}
      data-testid={testid}
      style={style}
    >
      {children}
    </header>
  );
};
